import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { AxiosError } from 'axios';
import { Landmark, AlertTriangle, Ban } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useLeoMutations } from '../hooks';
import { LEO_TYPE_VALUES } from '../types';
import type { DuplicateMatch, LeoEntry, LeoType } from '../types';

interface DuplicateConflict {
  level: 'warning' | 'block';
  matches: DuplicateMatch[];
}

interface LeoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null → create; an entry → edit */
  entry: LeoEntry | null;
}

/**
 * Case 360° (Lot 2) Stage 8 — create / edit a LEO entry. The server runs the
 * fuzzy duplicate guard: a WARNING (80–94% similar) is shown here and can be
 * acknowledged ("create anyway" → force=true); a BLOCK (≥95% + same city) is
 * final — use the existing entry or ask a curator to merge.
 */
export function LeoDialog({ open, onOpenChange, entry }: LeoDialogProps) {
  const { t } = useTranslation();
  const { createMutation, updateMutation } = useLeoMutations();

  const [name, setName] = React.useState('');
  const [type, setType] = React.useState<LeoType>('COURT');
  const [city, setCity] = React.useState('');
  const [address, setAddress] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [conflict, setConflict] = React.useState<DuplicateConflict | null>(null);

  React.useEffect(() => {
    if (open) {
      setName(entry?.name ?? '');
      setType(entry?.type ?? 'COURT');
      setCity(entry?.city ?? '');
      setAddress(entry?.address ?? '');
      setPhone(entry?.phone ?? '');
      setNotes(entry?.notes ?? '');
      setConflict(null);
    }
  }, [open, entry]);

  const isPending = createMutation.isPending || updateMutation.isPending;
  const canSubmit = !!name.trim() && !!city.trim() && !isPending;

  const readConflict = (error: unknown): DuplicateConflict | null => {
    const data = (error as AxiosError)?.response?.data as
      | { level?: string; matches?: DuplicateMatch[] }
      | undefined;
    if (!data?.matches?.length) return null;
    return {
      level: data?.level === 'warning' ? 'warning' : 'block',
      matches: data.matches,
    };
  };

  const handleSubmit = async (force = false) => {
    if (!canSubmit) return;
    const payload = {
      name: name.trim(),
      type,
      city: city.trim(),
      ...(address.trim() ? { address: address.trim() } : {}),
      ...(phone.trim() ? { phone: phone.trim() } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    };
    try {
      if (entry) {
        await updateMutation.mutateAsync({ id: entry.id, data: payload });
      } else {
        await createMutation.mutateAsync({ ...payload, ...(force ? { force } : {}) });
      }
      onOpenChange(false);
    } catch (error) {
      const c = readConflict(error);
      if (c) setConflict(c);
      else setConflict({ level: 'block', matches: [] });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Landmark className="h-4 w-4 text-slate-500" />
            {entry ? t('leo.editDialogTitle') : t('leo.addDialogTitle')}
          </DialogTitle>
          <DialogDescription>{t('leo.dialogDescription')}</DialogDescription>
        </DialogHeader>

        <div className="my-2 space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium">{t('leo.nameLabel')}</label>
            <Input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setConflict(null);
              }}
              placeholder={t('leo.namePlaceholder')}
              maxLength={300}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-2 block text-sm font-medium">{t('leo.typeLabel')}</label>
              <Select value={type} onValueChange={(v) => setType(v as LeoType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEO_TYPE_VALUES.map((lt) => (
                    <SelectItem key={lt} value={lt}>
                      {t(`leo.type.${lt}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium">{t('leo.cityLabel')}</label>
              <Input
                value={city}
                onChange={(e) => {
                  setCity(e.target.value);
                  setConflict(null);
                }}
                placeholder={t('leo.cityPlaceholder')}
                maxLength={100}
              />
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">{t('leo.addressLabel')}</label>
            <Input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              maxLength={500}
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">{t('leo.phoneLabel')}</label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={50} />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">{t('leo.notesLabel')}</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={2000}
            />
          </div>

          {/* Fuzzy duplicate guard result */}
          {conflict && (
            <div
              className={`rounded-md border px-3 py-2 text-sm ${
                conflict.level === 'block'
                  ? 'border-red-300 bg-red-50 text-red-700'
                  : 'border-amber-300 bg-amber-50 text-amber-700'
              }`}
            >
              <div className="flex items-center gap-2 font-medium">
                {conflict.level === 'block' ? (
                  <Ban className="h-4 w-4" />
                ) : (
                  <AlertTriangle className="h-4 w-4" />
                )}
                {conflict.level === 'block'
                  ? t('leo.duplicateBlocked')
                  : t('leo.duplicateWarning')}
              </div>
              {conflict.matches.length > 0 && (
                <ul className="mt-1 space-y-0.5 text-xs">
                  {conflict.matches.map((m) => (
                    <li key={m.id}>
                      {m.name} — {m.city} ({Math.round((m.similarity ?? 0) * 100)}%)
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            {t('cancel')}
          </Button>
          {conflict?.level === 'warning' && !entry ? (
            <Button
              variant="destructive"
              onClick={() => void handleSubmit(true)}
              disabled={!canSubmit}
            >
              {isPending ? t('processing') : t('leo.createAnyway')}
            </Button>
          ) : (
            <Button onClick={() => void handleSubmit()} disabled={!canSubmit || conflict?.level === 'block'}>
              {isPending ? t('processing') : t('leo.save')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
